import { useMemo, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { Badge, Button, DateRangePicker, Drawer, Input, Select, Spinner, Table, Tabs, type TableColumn } from '@/ui'
import { SHARED_RANGE_PRESETS_WITH_ALL } from '@/lib/dateRange'
import { format } from 'date-fns'
import styles from './SolapiLog.module.css'

// ── 타입 ─────────────────────────────────────────────────────────

type Tab = 'groups' | 'messages'

const TAB_ITEMS: { key: Tab; label: string }[] = [
  { key: 'groups', label: '전송요청내역' },
  { key: 'messages', label: '메시지목록' },
]

interface GroupRow {
  groupId: string
  status: string
  dateCreated: string
  dateUpdated: string
  count?: { total?: number; sentSuccess?: number; sentFailed?: number; sentPending?: number; replacement?: number }
  log?: Array<{ message?: string; createAt?: string }>
  price?: { sum?: number }
  point?: { sum?: number }
}

interface MessageRow {
  messageId: string
  groupId: string
  to: string
  from: string
  type: string
  statusCode: string
  statusMessage: string | null
  dateProcessed: string | null
  dateReceived: string | null
  text?: string | null
  subject?: string | null
  kakaoOptions?: { templateId?: string } | null
}

interface GroupsResponse { groups: GroupRow[]; nextKey: string | null; limit: number | null }
interface MessagesResponse { messages: MessageRow[]; nextKey: string | null; limit: number | null; filtered?: boolean; rawCount?: number }

type BadgeColor = 'gray' | 'blue' | 'green' | 'red' | 'yellow'

// ── 상수 ─────────────────────────────────────────────────────────

const GROUP_STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'COMPLETE', label: '처리완료' },
  { value: 'SCHEDULED', label: '예약대기' },
  { value: 'SENDING', label: '발송중' },
  { value: 'PENDING', label: '대기' },
  { value: 'FAILED', label: '실패' },
]

const MESSAGE_TYPE_OPTIONS = [
  { value: '', label: '전체 타입' },
  { value: 'SMS', label: 'SMS' },
  { value: 'LMS', label: 'LMS' },
  { value: 'MMS', label: 'MMS' },
  { value: 'ATA', label: '알림톡 (ATA)' },
  { value: 'CTA', label: '친구톡 (CTA)' },
]

// 발송 용도 — 서버가 kakaoOptions.templateId로 후처리 필터링(만료/RSVP/인증).
const MESSAGE_CATEGORY_OPTIONS = [
  { value: '', label: '전체 용도' },
  { value: 'expiry', label: '만료 임박 알림' },
  { value: 'rsvp', label: 'RSVP 요약' },
  { value: 'verification', label: '본인인증' },
]

const STATUS_CODE_LABEL: Record<string, string> = {
  '4000': '발송완료',
  '3104': '카톡 미사용자',
  '3014': '발송실패',
  '2000': '발송대기',
  '1042': '템플릿 불일치',
  '1041': '플친 차단',
  '1062': '발신번호 미등록',
}

function statusCodeColor(code: string): BadgeColor {
  if (code.startsWith('4')) return 'green'
  if (code.startsWith('2')) return 'blue'
  if (code.startsWith('1') || code.startsWith('3')) return 'red'
  return 'yellow'
}

function groupStatusColor(s: string): BadgeColor {
  switch (s) {
    case 'COMPLETE': return 'green'
    case 'FAILED': return 'red'
    case 'SCHEDULED':
    case 'PENDING': return 'yellow'
    case 'SENDING': return 'blue'
    default: return 'gray'
  }
}

const DATE_FMT = 'MM/dd HH:mm:ss'

// 빠른검색 프리셋 — admin 공용(SHARED_RANGE_PRESETS_WITH_ALL). '전체'는 기간 해제.

/** YYYY-MM-DD → 해당 일자 KST 자정/막차 → ISO. 빈 값이면 undefined. */
function dateInputToIso(raw: string, endOfDay = false): string | undefined {
  if (!raw) return undefined
  const [y, m, d] = raw.split('-').map(Number)
  if (!y || !m || !d) return undefined
  const utcHour = endOfDay ? 14 : -9
  const utcMin = endOfDay ? 59 : 0
  const utcSec = endOfDay ? 59 : 0
  const dt = new Date(Date.UTC(y, m - 1, d, utcHour, utcMin, utcSec))
  return dt.toISOString()
}

function truncate(s: string | null | undefined, n: number): string {
  if (!s) return ''
  return s.length > n ? s.slice(0, n) + '…' : s
}

function fmt(d: string | null | undefined): string {
  if (!d) return '-'
  try { return format(new Date(d), DATE_FMT) } catch { return d }
}

// ── 페이지 ───────────────────────────────────────────────────────

export function SolapiLog() {
  const [sp, setSp] = useSearchParams()
  const tab = (sp.get('tab') as Tab) ?? 'groups'
  const startDate = sp.get('start') ?? ''
  const endDate = sp.get('end') ?? ''
  const groupStatus = sp.get('gstatus') ?? ''
  const msgType = sp.get('mtype') ?? ''
  const msgTo = sp.get('to') ?? ''
  const msgStatusCode = sp.get('code') ?? ''
  const msgCategory = sp.get('mcat') ?? ''

  // 페이징 — Solapi cursor 기반. 페이지 스택은 메모리 한정.
  const [keyStack, setKeyStack] = useState<string[]>([])
  const currentStartKey = keyStack[keyStack.length - 1]
  const resetPaging = useCallback(() => setKeyStack([]), [])

  const setFilter = (key: string, value: string) => {
    if (value) sp.set(key, value)
    else sp.delete(key)
    setSp(sp, { replace: true })
    resetPaging()
  }

  // 상세 Drawer
  const [detail, setDetail] = useState<GroupRow | MessageRow | null>(null)
  const [detailKind, setDetailKind] = useState<Tab>('groups')

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <h1 className={styles.title}>Solapi 메시지 로그</h1>
        <p className={styles.subtitle}>모든 발송 내역과 메시지는 Solapi에서 6개월간 보관됩니다.</p>
      </header>

      <Tabs
        items={TAB_ITEMS}
        value={tab}
        onChange={(key) => {
          if (key === 'groups') sp.delete('tab')
          else sp.set('tab', key)
          setSp(sp, { replace: true })
          resetPaging()
        }}
      />

      <div className={styles.filters}>
        <DateRangePicker
          label="기간"
          size="sm"
          value={{ from: startDate || null, to: endDate || null }}
          presets={SHARED_RANGE_PRESETS_WITH_ALL}
          onChange={(range) => {
            if (range.from) sp.set('start', range.from); else sp.delete('start')
            if (range.to) sp.set('end', range.to); else sp.delete('end')
            setSp(sp, { replace: true })
            resetPaging()
          }}
        />
        {tab === 'groups' ? (
          <Select
            label="상태"
            value={groupStatus}
            onChange={(e) => setFilter('gstatus', e.target.value)}
            options={GROUP_STATUS_OPTIONS}
            size="sm"
          />
        ) : (
          <>
            <Select
              label="용도"
              value={msgCategory}
              onChange={(e) => setFilter('mcat', e.target.value)}
              options={MESSAGE_CATEGORY_OPTIONS}
              size="sm"
            />
            <Select
              label="메시지 타입"
              value={msgType}
              onChange={(e) => setFilter('mtype', e.target.value)}
              options={MESSAGE_TYPE_OPTIONS}
              size="sm"
            />
            <Input
              label="수신번호"
              placeholder="01012345678"
              value={msgTo}
              onChange={(e) => setFilter('to', e.target.value)}
              size="sm"
            />
            <Input
              label="상태코드"
              placeholder="4000"
              value={msgStatusCode}
              onChange={(e) => setFilter('code', e.target.value)}
              size="sm"
            />
          </>
        )}
      </div>

      {tab === 'groups' ? (
        <GroupsTab
          startDate={startDate}
          endDate={endDate}
          status={groupStatus}
          startKey={currentStartKey}
          keyStack={keyStack}
          setKeyStack={setKeyStack}
          onRowClick={(row) => {
            setDetailKind('groups')
            setDetail(row)
          }}
        />
      ) : (
        <MessagesTab
          startDate={startDate}
          endDate={endDate}
          type={msgType}
          to={msgTo}
          statusCode={msgStatusCode}
          category={msgCategory}
          startKey={currentStartKey}
          keyStack={keyStack}
          setKeyStack={setKeyStack}
          onRowClick={(row) => {
            setDetailKind('messages')
            setDetail(row)
          }}
        />
      )}

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detailKind === 'groups' ? '그룹 상세' : '메시지 상세'}
        size="lg"
      >
        {detail && (
          <pre className={styles.detailJson}>{JSON.stringify(detail, null, 2)}</pre>
        )}
      </Drawer>
    </div>
  )
}

// ── 그룹 탭 ──────────────────────────────────────────────────────

function GroupsTab(props: {
  startDate: string
  endDate: string
  status: string
  startKey: string | undefined
  keyStack: string[]
  setKeyStack: (s: string[]) => void
  onRowClick: (row: GroupRow) => void
}) {
  const { startDate, endDate, status, startKey, keyStack, setKeyStack, onRowClick } = props

  const path = useMemo(() => {
    const qs = new URLSearchParams()
    const s = dateInputToIso(startDate, false)
    const e = dateInputToIso(endDate, true)
    if (s) qs.set('startDate', s)
    if (e) qs.set('endDate', e)
    if (status) qs.set('status', status)
    if (startKey) qs.set('startKey', startKey)
    qs.set('limit', '50')
    return `/api/admin/solapi/groups?${qs.toString()}`
  }, [startDate, endDate, status, startKey])

  const { data, isLoading, error } = useApiFetch<GroupsResponse>(path)

  const columns: TableColumn<GroupRow>[] = [
    {
      key: 'dateCreated',
      label: '생성일',
      width: '150px',
      render: (r) => <span className={styles.mono}>{fmt(r.dateCreated)}</span>,
    },
    {
      key: 'status',
      label: '상태',
      width: '110px',
      render: (r) => <Badge color={groupStatusColor(r.status)}>{r.status}</Badge>,
    },
    {
      key: 'count',
      label: '건수',
      width: '220px',
      render: (r) => (
        <span className={styles.counts}>
          <span>총 {r.count?.total ?? 0}</span>
          {(r.count?.sentSuccess ?? 0) > 0 && <span className={styles.success}>· 성공 {r.count?.sentSuccess}</span>}
          {(r.count?.sentFailed ?? 0) > 0 && <span className={styles.fail}>· 실패 {r.count?.sentFailed}</span>}
          {(r.count?.sentPending ?? 0) > 0 && <span className={styles.pending}>· 대기 {r.count?.sentPending}</span>}
        </span>
      ),
    },
    {
      key: 'log',
      label: '최근 로그',
      render: (r) => <span className={styles.muted}>{truncate(r.log?.[r.log.length - 1]?.message, 60)}</span>,
    },
    {
      key: 'groupId',
      label: 'Group ID',
      width: '180px',
      render: (r) => <code className={styles.code}>{r.groupId}</code>,
    },
  ]

  if (error) return <div className={styles.error}>조회 실패: {error}</div>

  return (
    <>
      <Table
        columns={columns}
        data={data?.groups ?? []}
        rowKey={(r) => r.groupId}
        onRowClick={onRowClick}
        isLoading={isLoading}
        emptyMessage="조건에 맞는 발송 그룹이 없습니다"
      />
      <Pager
        keyStack={keyStack}
        setKeyStack={setKeyStack}
        nextKey={data?.nextKey ?? null}
        disabled={isLoading}
      />
    </>
  )
}

// ── 메시지 탭 ────────────────────────────────────────────────────

function MessagesTab(props: {
  startDate: string
  endDate: string
  type: string
  to: string
  statusCode: string
  category: string
  startKey: string | undefined
  keyStack: string[]
  setKeyStack: (s: string[]) => void
  onRowClick: (row: MessageRow) => void
}) {
  const { startDate, endDate, type, to, statusCode, category, startKey, keyStack, setKeyStack, onRowClick } = props

  const path = useMemo(() => {
    const qs = new URLSearchParams()
    const s = dateInputToIso(startDate, false)
    const e = dateInputToIso(endDate, true)
    if (s) qs.set('startDate', s)
    if (e) qs.set('endDate', e)
    if (type) qs.set('type', type)
    if (to) qs.set('to', to)
    if (statusCode) qs.set('statusCode', statusCode)
    if (category) qs.set('category', category)
    if (startKey) qs.set('startKey', startKey)
    qs.set('limit', '50')
    return `/api/admin/solapi/messages?${qs.toString()}`
  }, [startDate, endDate, type, to, statusCode, category, startKey])

  const { data, isLoading, error } = useApiFetch<MessagesResponse>(path)

  const columns: TableColumn<MessageRow>[] = [
    {
      key: 'dateProcessed',
      label: '발송일시',
      width: '150px',
      render: (r) => <span className={styles.mono}>{fmt(r.dateProcessed)}</span>,
    },
    {
      key: 'type',
      label: '타입',
      width: '70px',
      render: (r) => <Badge color="blue">{r.type}</Badge>,
    },
    { key: 'from', label: '발신번호', width: '130px', render: (r) => <span className={styles.mono}>{r.from}</span> },
    { key: 'to', label: '수신번호', width: '130px', render: (r) => <span className={styles.mono}>{r.to}</span> },
    {
      key: 'statusCode',
      label: '상태',
      width: '160px',
      render: (r) => (
        <Badge color={statusCodeColor(r.statusCode)}>
          {STATUS_CODE_LABEL[r.statusCode] ?? r.statusCode}
          <span className={styles.codeNum}> ({r.statusCode})</span>
        </Badge>
      ),
    },
    {
      key: 'text',
      label: '내용',
      render: (r) => <span className={styles.content}>{truncate(r.text, 80)}</span>,
    },
  ]

  if (error) return <div className={styles.error}>조회 실패: {error}</div>

  return (
    <>
      {data?.filtered && (
        <div className={styles.note}>
          용도 필터는 이 페이지에서 받아온 내역({data.rawCount ?? 0}건) 중 일치 건만 표시합니다.
          SMS로 대체발송된 일부 알림은 누락될 수 있으니 기간을 좁혀 조회하세요.
        </div>
      )}
      <Table
        columns={columns}
        data={data?.messages ?? []}
        rowKey={(r) => r.messageId}
        onRowClick={onRowClick}
        isLoading={isLoading}
        emptyMessage="조건에 맞는 메시지가 없습니다"
      />
      <Pager
        keyStack={keyStack}
        setKeyStack={setKeyStack}
        nextKey={data?.nextKey ?? null}
        disabled={isLoading}
      />
    </>
  )
}

// ── 페이지네이션 (cursor 기반) ──────────────────────────────────

function Pager(props: {
  keyStack: string[]
  setKeyStack: (s: string[]) => void
  nextKey: string | null
  disabled: boolean
}) {
  const { keyStack, setKeyStack, nextKey, disabled } = props
  const page = keyStack.length + 1
  return (
    <div className={styles.pager}>
      <Button
        size="sm"
        variant="secondary"
        disabled={keyStack.length === 0 || disabled}
        onClick={() => setKeyStack(keyStack.slice(0, -1))}
      >
        ‹ 이전
      </Button>
      <span className={styles.pageLabel}>
        페이지 {page}
        {disabled && <Spinner size="sm" />}
      </span>
      <Button
        size="sm"
        variant="secondary"
        disabled={!nextKey || disabled}
        onClick={() => nextKey && setKeyStack([...keyStack, nextKey])}
      >
        다음 ›
      </Button>
    </div>
  )
}
